import type { MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { CoachEntity } from "@modules/profile/coachProfile/coachEntity"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import {
  type LightProgramBundleInfoDto,
  type ProgramBundleDraftDto,
  type ProgramBundleDto,
  type ProgramBundleItemDto,
  type ProgramBundleLightDto,
  ProgramBundleType,
} from "@modules/programBundle/api/programBundleDto"
import { UserProgramItemEntity } from "@modules/programs/entities/userProgramItemEntity"
import { ProgramVisibility } from "@modules/programs/types/programTypes"
import { type HUImage } from "@modules/types/imageTypes"

export class ProgramBundleDraftEntity {
  name: string
  description: string
  visibility: ProgramVisibility
  type: ProgramBundleType
  image: MediaItemFormType | null
  price: PriceObjectEntity | null
  priceInput: number | null // Use only for the form
  programIds: string[]
  addFreeTrial: boolean
  trialDuration: number | null

  constructor(programBundle: ProgramBundleDraftEntity) {
    this.type = programBundle.type
    this.name = programBundle.name
    this.description = programBundle.description
    this.visibility = programBundle.visibility
    this.image = programBundle.image
    this.price = programBundle.price
    this.programIds = programBundle.programIds
    this.priceInput = programBundle.price ? programBundle.price.value : null
    this.addFreeTrial = !!programBundle.trialDuration
    this.trialDuration = programBundle.trialDuration
  }

  static createDraft(): ProgramBundleDraftEntity {
    return new ProgramBundleDraftEntity({
      type: ProgramBundleType.VIRTUAL,
      name: "",
      description: "",
      visibility: ProgramVisibility.PRIVATE,
      image: null,
      price: null,
      priceInput: null,
      programIds: [],
      addFreeTrial: false,
      trialDuration: null,
    })
  }

  static fromProgramBundleEntity(programBundle: ProgramBundleEntity): ProgramBundleDraftEntity {
    return new ProgramBundleDraftEntity({
      type: programBundle.type,
      name: programBundle.name,
      description: programBundle.description,
      visibility: programBundle.visibility,
      image: programBundle.image
        ? {
            url: programBundle.image.url,
            key: programBundle.image.key ?? null,
            type: MediaType.S3_IMAGE,
            filename: null,
            isUploading: false,
            file: null,
          }
        : null,
      price: programBundle.price ? PriceObjectEntity.fromDto(programBundle.price) : null,
      programIds: programBundle.programs?.map((program) => program.id) ?? [],
      priceInput: programBundle.price ? programBundle.price.value : null,
      addFreeTrial: !!programBundle.trialDuration,
      trialDuration: programBundle.trialDuration,
    })
  }

  static toDto(programBundle: ProgramBundleDraftEntity): ProgramBundleDraftDto {
    return {
      type: programBundle.type,
      name: programBundle.name,
      description: programBundle.description,
      visibility: programBundle.visibility,
      imageKey: programBundle.image?.key ?? null,
      price: programBundle.price ? PriceObjectEntity.toDto(programBundle.price) : null,
      programIds: programBundle.programIds,
      trialDuration: programBundle.trialDuration ?? null,
    }
  }
}

export class ProgramBundleEntity {
  id: string
  type: ProgramBundleType
  name: string
  description: string
  visibility: ProgramVisibility
  image: HUImage | null
  invitationLink?: string | null
  owner: CoachEntity
  subscriptionCount: number
  price: PriceObjectEntity | null
  programs?: UserProgramItemEntity[]
  nbPrograms: number
  trialDuration: number | null
  isEligibleToTrial: boolean | null

  private constructor(programBundle: ProgramBundleEntity) {
    this.id = programBundle.id
    this.type = programBundle.type
    this.name = programBundle.name
    this.description = programBundle.description
    this.visibility = programBundle.visibility
    this.image = programBundle.image
    this.invitationLink = programBundle.invitationLink
    this.owner = programBundle.owner
    this.subscriptionCount = programBundle.subscriptionCount
    this.price = programBundle.price
    this.programs = programBundle.programs
    this.nbPrograms = programBundle.nbPrograms
    this.trialDuration = programBundle.trialDuration
    this.isEligibleToTrial = programBundle.isEligibleToTrial
  }

  static fromDto(dto: ProgramBundleDto): ProgramBundleEntity {
    return new ProgramBundleEntity({
      ...dto,
      price: dto.price ? PriceObjectEntity.fromDto(dto.price) : null,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      owner: CoachEntity.fromDto(dto.owner),
      programs: dto.programs.map((program) => UserProgramItemEntity.fromDto(program)),
      type: dto.type,
    })
  }
}

export class ProgramBundleItemEntity {
  id: string
  type: ProgramBundleType
  name: string
  visibility: ProgramVisibility
  image: HUImage | null
  price: PriceObjectEntity | null
  subscriptionCount: number
  nbPrograms: number
  owner: UserEntity

  private constructor(programBundle: ProgramBundleItemEntity) {
    this.id = programBundle.id
    this.type = programBundle.type
    this.name = programBundle.name
    this.image = programBundle.image
    this.price = programBundle.price
    this.subscriptionCount = programBundle.subscriptionCount
    this.nbPrograms = programBundle.nbPrograms
    this.visibility = programBundle.visibility
    this.owner = programBundle.owner
  }

  static fromDto(dto: ProgramBundleItemDto): ProgramBundleItemEntity {
    return new ProgramBundleItemEntity({
      ...dto,
      price: dto.price ? PriceObjectEntity.fromDto(dto.price) : null,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      owner: UserEntity.fromDto(dto.owner),
    })
  }
}

export class ProgramBundleLightEntity {
  programBundleId: string
  programBundleName: string
  paidPrice: PriceObjectEntity | null
  type: ProgramBundleType

  constructor(programBundle: ProgramBundleLightEntity) {
    this.programBundleId = programBundle.programBundleId
    this.programBundleName = programBundle.programBundleName
    this.paidPrice = programBundle.paidPrice
    this.type = programBundle.type
  }

  static fromDto(dto: ProgramBundleLightDto, type: ProgramBundleType): ProgramBundleLightEntity {
    return new ProgramBundleLightEntity({
      programBundleId: dto.programBundleId,
      programBundleName: dto.programBundleName,
      paidPrice: dto.paidPrice ? PriceObjectEntity.fromDto(dto.paidPrice) : null,
      type,
    })
  }
}

export class LightProgramBundleInfo {
  id: string
  name: string
  image: HUImage | null
  price: PriceObjectEntity | null
  type: ProgramBundleType

  private constructor(programBundle: LightProgramBundleInfo) {
    this.id = programBundle.id
    this.name = programBundle.name
    this.image = programBundle.image
    this.price = programBundle.price
    this.type = programBundle.type
  }

  static fromDto(dto: LightProgramBundleInfoDto): LightProgramBundleInfo {
    return new LightProgramBundleInfo({
      ...dto,
      image: dto.image ? convertImageFromDto(dto.image) : null,
      price: dto.price ? PriceObjectEntity.fromDto(dto.price) : null,
    })
  }
}
